import type { KeyboardEvent, WheelEvent } from 'react';
import { TextFieldElement, TextFieldElementProps } from 'react-hook-form-mui';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ServiceThemeOptions } from '../themes';

export interface StyledTextFieldProps extends TextFieldElementProps {
  fieldSx?: object;
}

export default function StyledTextField({
  fieldSx,
  inputProps,
  ...props
}: StyledTextFieldProps) {
  const theme = useTheme() as ServiceThemeOptions;

  const isNumberField = props.type === 'number';

  const mergedInputProps = isNumberField
    ? {
        ...inputProps,
        inputMode: inputProps?.inputMode ?? 'numeric',
        onWheel: (event: WheelEvent<HTMLInputElement>) => {
          event.preventDefault();
          inputProps?.onWheel?.(event);
        },
        onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
          }
          inputProps?.onKeyDown?.(event);
        },
      }
    : inputProps;

  // Extract theme colors safely
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const borderDefault = theme.palette?.grey?.[300] || '#C7CACB';
  const labelDefault = theme.palette?.grey?.[600] || '#70787C';
  const fontFamily =
    (theme.typography as any)?.fontFamily || 'Inter, sans-serif';

  // Use new theme-aware input field colors with fallbacks
  const focusBorderColor =
    theme.custom?.inputField?.focus?.borderColor || primaryColor;
  const focusBorderWidth =
    theme.custom?.inputField?.focus?.borderWidth || '2px';
  const errorBorderColor =
    theme.custom?.inputField?.error?.borderColor ||
    (theme.palette?.error as any)?.main ||
    '#D32F2F';
  const errorBorderWidth =
    theme.custom?.inputField?.error?.borderWidth || '2px';
  const hoverBorderColor =
    theme.custom?.inputField?.hover?.borderColor ||
    (theme.palette?.text as any)?.primary ||
    '#263238';

  // Disable autocomplete for all fields since these are third-party TSP credentials
  // being entered into our Axle interface, not credentials for our own site
  const getAutocomplete = () => {
    return 'off';
  };

  // Define the consistent text field styling with theme awareness
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      height: '56px',
      borderRadius: '4px',
      padding: '16px 12px',
      '& fieldset': {
        borderColor: borderDefault,
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: hoverBorderColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: focusBorderColor,
        borderWidth: focusBorderWidth,
      },
      '&.Mui-error fieldset': {
        borderColor: errorBorderColor,
        borderWidth: errorBorderWidth,
      },
      '&.Mui-error.Mui-focused fieldset': {
        borderColor: errorBorderColor,
        borderWidth: errorBorderWidth,
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: fontFamily,
      color: labelDefault,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      fontWeight: 400,
      '&.Mui-focused': {
        color: focusBorderColor,
      },
      '&.Mui-error': {
        color: errorBorderColor,
      },
      '&.Mui-error.Mui-focused': {
        color: errorBorderColor,
      },
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(12px, -9px) scale(0.75)',
    },
    '& input': {
      fontSize: '16px',
      fontWeight: 400,
      letterSpacing: '0.15px',
      fontFamily: fontFamily,
      padding: '0',
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
      {
        '-webkit-appearance': 'none',
        margin: 0,
      },
    '& .MuiFormHelperText-root': {
      margin: '6px 8px 0',
      fontSize: '12px',
      lineHeight: '15px',
      letterSpacing: '0.4px',
    },
  };

  return (
    <Box sx={{ width: '100%', mb: 2, ...fieldSx }}>
      <TextFieldElement
        fullWidth
        sx={textFieldStyles}
        autoComplete={getAutocomplete()}
        inputProps={mergedInputProps}
        {...props}
      />
    </Box>
  );
}
